import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
  IconButton,
} from '@mui/material';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import DownloadIcon from '@mui/icons-material/Download';
import FolderIcon from '@mui/icons-material/Folder';
import PeopleIcon from '@mui/icons-material/People';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  Line,
  ComposedChart,
} from 'recharts';

import { statsAPI } from '../../services/adminApi';

const COLORS = ['#2563eb', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

const numFmt = new Intl.NumberFormat('it-IT');
const numFmtDec = new Intl.NumberFormat('it-IT', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

const formatSize = (bytes) => {
  if (bytes == null) return '-';
  const gb = bytes / (1024 * 1024 * 1024);
  if (gb >= 1) return `${numFmtDec.format(gb)} GB`;
  const mb = bytes / (1024 * 1024);
  if (mb >= 1) return `${numFmtDec.format(mb)} MB`;
  const kb = bytes / 1024;
  return `${numFmtDec.format(kb)} KB`;
};

const formatSizeMb = (mb) => {
  if (mb == null) return '-';
  if (mb >= 1024) return `${numFmtDec.format(mb / 1024)} GB`;
  return `${numFmtDec.format(mb)} MB`;
};

const StatCard = ({ icon, value, label, color }) => (
  <Paper
    elevation={0}
    sx={{
      border: 1,
      borderColor: 'divider',
      borderRadius: 2,
      p: 2.5,
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      height: '100%',
    }}
  >
    <Box
      sx={{
        width: 48,
        height: 48,
        borderRadius: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: `${color}15`,
        color: color,
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
        {value}
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>
        {label}
      </Typography>
    </Box>
  </Paper>
);

const ChartPaper = ({ title, children, sx: sxProp }) => (
  <Paper
    elevation={0}
    sx={{
      border: 1,
      borderColor: 'divider',
      borderRadius: 2,
      p: 2.5,
      height: '100%',
      ...sxProp,
    }}
  >
    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
      {title}
    </Typography>
    {children}
  </Paper>
);

const CustomPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  if (percent < 0.05) return null;
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text x={x} y={y} fill="#fff" textAnchor="middle" dominantBaseline="central" fontSize={12} fontWeight={600}>
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

export default function DashboardPage({ onBack }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [overview, setOverview] = useState(null);
  const [storage, setStorage] = useState(null);
  const [users, setUsers] = useState([]);
  const [filetypes, setFiletypes] = useState(null);
  const [topDownloads, setTopDownloads] = useState([]);

  useEffect(() => {
    let cancelled = false;

    const fetchAll = async () => {
      try {
        setLoading(true);
        setError(null);

        const [ovRes, stRes, usRes, ftRes, tdRes] = await Promise.all([
          statsAPI.overview(),
          statsAPI.storage(),
          statsAPI.users(),
          statsAPI.filetypes(),
          statsAPI.topDownloads(),
        ]);

        if (cancelled) return;

        setOverview(ovRes.data ?? ovRes);
        setStorage(stRes.data ?? stRes);
        setUsers(usRes.data ?? usRes);
        setFiletypes(ftRes.data ?? ftRes);
        setTopDownloads(tdRes.data ?? tdRes);
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.data?.detail || err.message || 'Errore nel caricamento dei dati');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchAll();
    return () => { cancelled = true; };
  }, []);

  const overviewCards = useMemo(() => {
    if (!overview) return [];
    return [
      {
        icon: <InsertDriveFileIcon />,
        value: numFmt.format(overview.total_files),
        label: 'Totale file',
        color: COLORS[0],
      },
      {
        icon: <CloudQueueIcon />,
        value: overview.total_size_gb >= 1
          ? `${numFmtDec.format(overview.total_size_gb)} GB`
          : `${numFmtDec.format(overview.total_size_mb)} MB`,
        label: 'Spazio occupato',
        color: COLORS[1],
      },
      {
        icon: <DownloadIcon />,
        value: numFmt.format(overview.total_downloads),
        label: 'Download totali',
        color: COLORS[2],
      },
      {
        icon: <FolderIcon />,
        value: numFmt.format(overview.total_folders),
        label: 'Cartelle',
        color: COLORS[3],
      },
      {
        icon: <PeopleIcon />,
        value: numFmt.format(overview.total_users),
        label: 'Utenti attivi',
        color: COLORS[4],
      },
      {
        icon: <QrCode2Icon />,
        value: numFmt.format(overview.files_with_qr),
        label: 'File con QR',
        color: COLORS[5],
      },
    ];
  }, [overview]);

  const monthlyData = useMemo(() => {
    if (!storage?.timeline_monthly) return [];
    return storage.timeline_monthly.map((item) => ({
      ...item,
      label: item.month,
    }));
  }, [storage]);

  const dailyData = useMemo(() => {
    if (!storage?.timeline_daily) return [];
    return storage.timeline_daily.map((item) => ({
      ...item,
      label: item.date,
    }));
  }, [storage]);

  const categoryData = useMemo(() => {
    if (!filetypes?.by_category) return [];
    return filetypes.by_category;
  }, [filetypes]);

  const usersChartData = useMemo(() => {
    if (!users?.length) return [];
    return [...users]
      .sort((a, b) => b.files_count - a.files_count)
      .slice(0, 10)
      .map((u) => ({
        name: u.full_name || u.username,
        files: u.files_count,
        size_mb: u.total_size_mb,
      }));
  }, [users]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2, color: 'text.secondary' }}>Caricamento statistiche...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <Tooltip title="Indietro">
          <IconButton onClick={onBack} size="small">
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
        <TrendingUpIcon sx={{ color: 'primary.main' }} />
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Dashboard
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Row 1: Overview cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {overviewCards.map((card, idx) => (
          <Grid item xs={12} sm={6} md={4} lg={2} key={idx}>
            <StatCard {...card} />
          </Grid>
        ))}
      </Grid>

      {/* Row 2: Upload mensili + Distribuzione tipi file */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <ChartPaper title="Upload mensili">
            <Box sx={{ width: '100%', height: 320 }}>
              <ResponsiveContainer>
                <ComposedChart data={monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                  <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} />
                  <RechartsTooltip
                    formatter={(value, name) => {
                      if (name === 'size_mb') return [`${numFmtDec.format(value)} MB`, 'Dimensione'];
                      return [numFmt.format(value), 'File caricati'];
                    }}
                  />
                  <Legend
                    formatter={(value) => (value === 'count' ? 'File caricati' : 'Dimensione (MB)')}
                  />
                  <Bar yAxisId="left" dataKey="count" fill={COLORS[0]} radius={[4, 4, 0, 0]} barSize={24} />
                  <Line yAxisId="right" type="monotone" dataKey="size_mb" stroke={COLORS[2]} strokeWidth={2} dot={{ r: 3 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </Box>
          </ChartPaper>
        </Grid>

        <Grid item xs={12} md={6}>
          <ChartPaper title="Distribuzione tipi file">
            <Box sx={{ width: '100%', height: 320 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={categoryData}
                    dataKey="count"
                    nameKey="category"
                    cx="50%"
                    cy="50%"
                    outerRadius={110}
                    labelLine={false}
                    label={CustomPieLabel}
                  >
                    {categoryData.map((_, idx) => (
                      <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                    ))}
                  </Pie>
                  <RechartsTooltip
                    formatter={(value, name) => [numFmt.format(value), name]}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </ChartPaper>
        </Grid>
      </Grid>

      {/* Row 3: Upload giornalieri ultimi 30 giorni */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <ChartPaper title="Upload giornalieri - ultimi 30 giorni">
            <Box sx={{ width: '100%', height: 300 }}>
              <ResponsiveContainer>
                <AreaChart data={dailyData}>
                  <defs>
                    <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={COLORS[0]} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={COLORS[0]} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <RechartsTooltip
                    formatter={(value, name) => {
                      if (name === 'count') return [numFmt.format(value), 'File caricati'];
                      return [numFmtDec.format(value) + ' MB', 'Dimensione'];
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="count"
                    stroke={COLORS[0]}
                    strokeWidth={2}
                    fill="url(#colorCount)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          </ChartPaper>
        </Grid>
      </Grid>

      {/* Row 4: File per utente + Top download */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <ChartPaper title="File per utente">
            <Box sx={{ width: '100%', height: 360 }}>
              <ResponsiveContainer>
                <BarChart data={usersChartData} layout="vertical" margin={{ left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 12 }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={100} />
                  <RechartsTooltip
                    formatter={(value, name) => {
                      if (name === 'files') return [numFmt.format(value), 'File'];
                      return [numFmtDec.format(value) + ' MB', 'Dimensione'];
                    }}
                  />
                  <Bar dataKey="files" fill={COLORS[4]} radius={[0, 4, 4, 0]} barSize={18} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </ChartPaper>
        </Grid>

        <Grid item xs={12} md={6}>
          <ChartPaper title="Top download">
            <TableContainer sx={{ maxHeight: 360 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>File</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Cartella</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Download</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Dimensione</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">QR</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {topDownloads.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                        Nessun download registrato
                      </TableCell>
                    </TableRow>
                  ) : (
                    topDownloads.map((file) => (
                      <TableRow key={file.id} hover>
                        <TableCell>
                          <Tooltip title={file.filename}>
                            <Typography
                              variant="body2"
                              sx={{
                                maxWidth: 160,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {file.filename}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {file.folder || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            label={numFmt.format(file.download_count)}
                            size="small"
                            color="info"
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {formatSizeMb(file.file_size_mb)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={file.qr_downloaded ? 'Si' : 'No'}
                            size="small"
                            color={file.qr_downloaded ? 'success' : 'default'}
                            sx={{ fontWeight: 600, minWidth: 40 }}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </ChartPaper>
        </Grid>
      </Grid>
    </Box>
  );
}
